import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { SettingsService } from '../shared/services/admin-settings.service';
import { CurrentContextService } from '../core/current-context.service';
import { IdentityService } from '../core/identity/identity.service';
import { EarlyAccessFeatures } from '../core/identity/user-identity';
import { LocalStorageService } from '../shared/services/local-storage.service';

const MOBILE_MEDIA_QUERY = '(max-width: 600px)';
const CONTENT_PUSH_CLASS = 'agent-panel-open';
const WIDTH_STORAGE_KEY = 'agent-panel-width';
const WIDTH_CSS_VAR = '--agent-panel-width';
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 720;

// Open/closed + responsive state for the global agent panel; root singleton so launcher, sidebar and
// panel share one source of truth. `enabled` needs aiFeatures or Buzz Copilot early access, plus a session,
// so the assistant never renders pre-auth even if aiFeatures was left on in localStorage.
@Injectable({ providedIn: 'root' })
export class AgentPanelService {
  private readonly settings = inject(SettingsService);
  private readonly currentContext = inject(CurrentContextService);
  private readonly identity = inject(IdentityService);
  private readonly storage = inject(LocalStorageService);

  private readonly mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
  private readonly openState = signal(false);
  private readonly mobileState = signal(this.mobileMediaQuery.matches);

  // null → the responsive CSS default; a number overrides it via --agent-panel-width.
  private readonly widthState = signal<number | null>(null);

  // Handed over by a launcher elsewhere in the app; seeded unsent so it can still be edited.
  private readonly pendingDraftState = signal<string | null>(null);

  private readonly settingEnabled = toSignal(this.settings.settings$.pipe(map((settings) => settings.aiFeatures)), {
    initialValue: false,
  });
  // Latched: a brief null context during navigation must not close the panel; only sign-out clears
  // it. Starts false so nothing renders pre-auth.
  private readonly hasSession = signal(false);
  private readonly hasEarlyAccess = signal(false);

  readonly isOpen: Signal<boolean> = this.openState.asReadonly();
  readonly isMobile: Signal<boolean> = this.mobileState.asReadonly();
  readonly pendingDraft: Signal<string | null> = this.pendingDraftState.asReadonly();
  readonly enabled = computed(() => (this.settingEnabled() || this.hasEarlyAccess()) && this.hasSession());

  constructor() {
    const stored = this.storage.getItem<number>(WIDTH_STORAGE_KEY);
    if (typeof stored === 'number' && Number.isFinite(stored)) {
      this.widthState.set(clampWidth(stored));
    }

    effect(() => {
      const width = this.widthState();
      if (width === null) {
        document.documentElement.style.removeProperty(WIDTH_CSS_VAR);
      } else {
        document.documentElement.style.setProperty(WIDTH_CSS_VAR, `${width}px`);
      }
    });

    this.currentContext.currentContextBS.pipe(takeUntilDestroyed()).subscribe((context) => {
      if (context) {
        this.hasSession.set(true);
        this.hasEarlyAccess.set(context.user?.earlyAccessFeatures?.includes(EarlyAccessFeatures.BuzzCopilot) ?? false);
      }
    });
    this.identity.onSignout.pipe(takeUntilDestroyed()).subscribe(() => this.hasSession.set(false));

    this.mobileMediaQuery.addEventListener('change', (event) => this.mobileState.set(event.matches));

    // Force the panel shut whenever the assistant is turned off or the session ends. A queued draft
    // is dropped too, so it can't surface in a later session.
    effect(() => {
      if (!this.enabled()) {
        this.openState.set(false);
        this.pendingDraftState.set(null);
      }
    });

    // Desktop only: push page content aside via this body class (see app.component.scss); mobile
    // overlays full-width, so no push.
    effect(() => {
      document.body.classList.toggle(CONTENT_PUSH_CLASS, this.openState() && !this.mobileState());
    });
  }

  open(): void {
    if (!this.enabled()) {
      return;
    }
    this.openState.set(true);
  }

  // Replaces any queued draft rather than appending, so a launcher can call this per keystroke.
  openWithDraft(text: string): void {
    if (!this.enabled()) {
      return;
    }
    const trimmed = text.trim();
    if (trimmed.length > 0) {
      this.pendingDraftState.set(trimmed);
    }
    this.openState.set(true);
  }

  // Called once the panel has written the draft into the composer, so re-opening doesn't replay it.
  clearPendingDraft(): void {
    this.pendingDraftState.set(null);
  }

  close(): void {
    this.openState.set(false);
  }

  toggle(): void {
    if (!this.enabled()) {
      return;
    }
    this.openState.update((value) => !value);
  }

  setWidth(px: number): void {
    this.widthState.set(clampWidth(px));
  }

  persistWidth(): void {
    const width = this.widthState();
    if (width !== null) {
      this.storage.setItem(WIDTH_STORAGE_KEY, width);
    }
  }
}

// Capped so at least MIN_PANEL_WIDTH of page stays visible on a narrow window.
function clampWidth(px: number): number {
  const upperBound = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, window.innerWidth - MIN_PANEL_WIDTH));
  return Math.round(Math.min(Math.max(px, MIN_PANEL_WIDTH), upperBound));
}
